import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOM, { unmountComponentAtNode } from 'react-dom';

import moment from 'moment';
import _ from 'lodash';

import { Badge, Calendar, Spin, Radio, Select, Space, Typography, Button, Modal } from 'antd';
import { PlusOutlined, RightOutlined, LeftOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { confirm } = Modal;
const { Text } = Typography;

import './calendar-main.less';

import LineEvent from './custom-mode-components/custom-events/line-event';
import CalendarCustomMode from './custom-mode-components/calendar-custom-mode';

import CalendarSettings from './calendar-settings/calendar-settings';

import EditEventModal from './modals/edit-event/edit-event-modal';
import EventEditDeleteConfirmModal from './modals/edit-delete-event-confirmation/event-edit-delete-confirm-modal';

import * as calendarConsts from './../constants/calendar-constants';
import * as recurrence from './../constants/recurrence-constants';

import { getEventsOfYear, getEventsOfMonth, getCalendarHeaderMonthItems, getCalendarHeaderYearItems } from '../helpers/calendar-data-helper';
import { getSelectedDateRange } from './../utils/date-utils';

import * as actions from './../store/actions';

const CalendarMain = ({
  rootContainer,
  calendarCategories,
  sidebarSelectedCategories,
  sidebarSelectedGroups,
  sidebarSelectedDate,
  onSelectedDateChange,
  settings,
  onToggleEvents,
  onAddCategory,
}) => {
  const dispatch = useDispatch();

  const [calendarMode, setCalendarMode] = useState(calendarConsts.MONTH_MODE);
  const [navigationDate, setNavigationDate] = useState(moment());

  const [eventsSearchRequest, setEventsSearchRequest] = useState({
    agencyTaskChecked: null,
    userTaskChecked: null,
    dateFrom: null,
    dateTo: null,
    categories: [],
  });

  const [isEditEventModalOpen, setIsEditEventModalOpen] = useState(false);
  const [editEventModalTitle, setIsEditEventModalTitle] = useState('');
  const [eventToEdit, setEventToEdit] = useState(null);
  const [isEventEditDeleteConfirmModalOpen, setIsEventEditDeleteConfirmModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActiontype] = useState(recurrence.CONFIRM_ACTION_TYPES.NONE);
  const [confirmEditEventType, setConfirmEditEventType] = useState(null);

  const { calendarEventsLoading, calendarEvents, saveEventLoading } = useSelector((state) => state.calendarScheduler);

  useEffect(() => {
    if (sidebarSelectedCategories) {
      const request = eventsSearchRequest;
      request.categories = sidebarSelectedCategories;

      setEventsSearchRequest(request);
    }
  }, [sidebarSelectedCategories]);

  useEffect(() => {
    if (sidebarSelectedGroups && sidebarSelectedGroups.length > 0) {
      const request = eventsSearchRequest;
      const agencyOnly = _.includes(sidebarSelectedGroups, calendarConsts.AGENCY_TASKS);
      const userOnly = _.includes(sidebarSelectedGroups, calendarConsts.MY_TASKS);

      request.agencyTaskChecked = agencyOnly;
      request.userTaskChecked = userOnly;

      setEventsSearchRequest(request);
    }
  }, [sidebarSelectedGroups]);

  useEffect(() => {
    if (sidebarSelectedDate) {
      setNavigationDate(sidebarSelectedDate);

      setSearchDateRange(sidebarSelectedDate, calendarMode);
    }
  }, [sidebarSelectedDate]);

  useEffect(() => {
    if (calendarMode) {
      setSearchDateRange(navigationDate, calendarMode);
    }
  }, [calendarMode]);

  useEffect(() => {
    if (calendarMode && navigationDate && sidebarSelectedCategories && sidebarSelectedGroups && calendarCategories) {
      loadEvents(eventsSearchRequest);
    }
  }, [calendarMode, navigationDate, sidebarSelectedCategories, sidebarSelectedGroups, calendarCategories]);

  useEffect(() => {
    switchToCalendarMode(calendarMode);
  }, [calendarEvents]);

  const setSearchDateRange = (momentObj, mode) => {
    const [from, to] = getSelectedDateRange(momentObj, mode);
    const request = eventsSearchRequest;

    if (request.dateFrom !== from && request.dateTo !== to) {
      request.dateFrom = from.utc(false);
      request.dateTo = to.utc(false);

      setEventsSearchRequest(request);
    }
  };

  const loadEvents = (searchRequest) => {
    if ((searchRequest.agencyTaskChecked || searchRequest.userTaskChecked) && searchRequest.dateFrom && searchRequest.dateTo) {
      dispatch(actions.getCalendarEvents(searchRequest));
    }
  };

  const setCurrentlySelectedDate = (momentObj) => {
    if (typeof onSelectedDateChange === 'function') {
      onSelectedDateChange(momentObj);
    }
  };

  const switchToCalendarMode = (mode) => {
    if (calendarConsts.isCustomMode(mode)) {
      switchToCustomMode(mode);
    } else {
      switchFromCustomMode(mode);
    }
  };

  const dayCellRender = (momentDateObj) => {
    const [year, month, day] = [momentDateObj.year(), momentDateObj.month(), momentDateObj.date()];

    let listdata = getEventsOfMonth(year, month, day, calendarEvents);
    const eventsToShow = 3;
    return (
      <div
        className="ant-picker-cell-inner ant-picker-calendar-date"
        title={'Open ' + momentDateObj.format('LL')}
        onClick={($event) => {
          $event.preventDefault();
          $event.stopPropagation();
          onSelectCalendarCell(momentDateObj);
        }}
      >
        <div
          className="ant-picker-calendar-date-value"
          style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <div className="event-buttons">
            <Button
              title={'Add new event at ' + momentDateObj.format('LL')}
              type="default"
              icon={<PlusOutlined className="add-calendar-event-icon" />}
              onClick={($event) => handleAddEventClick($event, momentDateObj)}
            />
          </div>
          <span>{day}</span>
        </div>

        <div className="ant-picker-calendar-date-content">
          <div className="cell-events">
            {settings &&
              listdata
                .filter((ev, eventIndex) => settings.showAllEvents || (!settings.showAllEvents && eventIndex < eventsToShow))
                .map((eventItem) => (
                  <LineEvent
                    key={eventItem.id}
                    item={eventItem}
                    onEditEvent={handleEditEventClick}
                    onDeleteEvent={handleDeleteEventClick}
                    rootContainer={rootContainer}
                    calendarCategories={calendarCategories}
                  />
                ))}
            {settings && listdata && listdata.length > eventsToShow && (
              <Button
                title={settings.showAllEvents ? 'Hide few item(s)' : 'Show more item(s)'}
                type="link"
                size="small"
                className="more-items-btn-link"
                onClick={($event) => onToggleEvents($event)}
              >
                {/* {settings.showAllEvents ? 'Less item(s)' : 'More item(s)'} */}
                {settings.showAllEvents ? 'Less item(s)' : `+${listdata.length - eventsToShow} item(s)`}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleAddEventClick = (domEvent, momentDateObj, includeTime = false, defaultEventDurationMins = 60) => {
    domEvent.preventDefault();
    domEvent.stopPropagation();

    const startEventDate = !includeTime ? momentDateObj.startOf('day').clone().add(8, 'hours') : momentDateObj;

    setIsEditEventModalTitle('Add Event');
    setIsEditEventModalOpen(true);
    setEventToEdit({
      startObj: startEventDate.clone(),
      endObj: startEventDate.add(defaultEventDurationMins, 'minutes').clone(),
    });
  };

  const handleEditEventClick = (domEvent, event) => {
    domEvent.preventDefault();
    domEvent.stopPropagation();

    if (!event.recurrenceObject) {
      showEditModal(event, recurrence.EDIT_EVENT_TYPES.NONE);
    } else {
      showConfirmModal(event, recurrence.CONFIRM_ACTION_TYPES.EDIT);
    }
  };

  const handleConfirmModalOk = (confirmationActionType, event, type) => {
    setIsEventEditDeleteConfirmModalOpen(false);
    setConfirmEditEventType(!type ? recurrence.EDIT_EVENT_TYPES.NONE : type);

    if (confirmationActionType && confirmationActionType === recurrence.CONFIRM_ACTION_TYPES.DELETE) {
      if (!type || type === recurrence.EDIT_EVENT_TYPES.NONE) {
        handleDeleteEvent(event, type);
      } else {
        handleDeleteEvent(event, type);
      }
    } else if (confirmationActionType && confirmationActionType === recurrence.CONFIRM_ACTION_TYPES.EDIT) {
      showEditModal(event, type);
    }
  };

  const showEditModal = (event, type) => {
    if (type && (type === recurrence.EDIT_EVENT_TYPES.THIS || type === recurrence.EDIT_EVENT_TYPES.THIS_AND_FOLLOWING)) {
      event.isCheckedRecurrent = false;
    }

    setIsEditEventModalTitle('Edit Event');
    setIsEditEventModalOpen(true);
    setEventToEdit(_.cloneDeep(event));
  };

  const showConfirmModal = (eventItem, confirmActionType) => {
    setEventToEdit(_.cloneDeep(eventItem));
    setIsEventEditDeleteConfirmModalOpen(true);
    setConfirmationActiontype(confirmActionType);
  };

  const handleDeleteEventClick = (eventItem) => {
    if (!eventItem.recurrenceObject) {
      confirm({
        getContainer: rootContainer,
        title: `Are you sure you want to delete '${eventItem.subject}' event?`,
        icon: <ExclamationCircleOutlined />,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          setConfirmEditEventType(recurrence.EDIT_EVENT_TYPES.NONE);
          handleDeleteEvent(eventItem, recurrence.EDIT_EVENT_TYPES.NONE);
        },
      });
    } else {
      showConfirmModal(eventItem, recurrence.CONFIRM_ACTION_TYPES.DELETE);
    }
  };

  const handleDeleteEvent = (eventItem, type) => {
    let id = null;
    if (!type || type === recurrence.EDIT_EVENT_TYPES.NONE) {
      id = eventItem.id;
    } else {
      id = eventItem.recurrenceParentId;
    }
    const command = {
      id: id,
      type: type,
      start: eventItem.startObj,
      end: eventItem.endObj,
    };

    return new Promise((resolve, reject) => {
      dispatch(actions.deleteEvent(command)).then(() => {
        loadEvents(eventsSearchRequest);
        setConfirmEditEventType(recurrence.EDIT_EVENT_TYPES.NONE);
        resolve();
      });
    });
  };

  const handleConfirmModalCancel = () => {
    setIsEventEditDeleteConfirmModalOpen(false);
    setEventToEdit(null);
    setConfirmEditEventType(recurrence.EDIT_EVENT_TYPES.NONE);
  };

  const monthCellRender = (momentDateObj) => {
    let listData = getEventsOfYear(momentDateObj, calendarEvents);

    return listData.length > 0 ? (
      <Space className="month-events-content">
        <Badge count={listData.length} style={{ backgroundColor: '#52c41a' }} />

        <Text type="success">event(s)</Text>
      </Space>
    ) : null;
  };

  const switchToCustomMode = (mode) => {
    const calendar = document.querySelector('.calendar-main');

    if (calendar) {
      const calendarPanel = calendar.querySelector('.ant-picker-body');

      if (calendarPanel) {
        calendarPanel.style.display = 'none';

        if (!document.getElementById('custom-mode-root')) {
          const newNode = document.createElement('div');

          newNode.id = 'custom-mode-root';

          calendarPanel.parentNode.insertBefore(newNode, calendarPanel.nextSibling);
        }

        ReactDOM.render(
          <CalendarCustomMode
            currentDate={navigationDate}
            dateFrom={eventsSearchRequest && eventsSearchRequest.dateFrom}
            customMode={mode}
            calendarEvents={calendarEvents}
            rootContainer={rootContainer}
            eventLoading={calendarEventsLoading}
            onEditEvent={handleEditEventClick}
            onAddEvent={handleAddEventClick}
            onDeleteEvent={handleDeleteEventClick}
            calendarCategories={calendarCategories}
            settings={settings}
            onToggleEvents={onToggleEvents}
          />,
          document.getElementById('custom-mode-root'),
        );
      }
    }
  };

  const switchFromCustomMode = () => {
    const calendar = document.querySelector('.calendar-main');

    if (calendar) {
      const calendarPanel = calendar.querySelector('.ant-picker-body');

      if (calendarPanel) {
        calendarPanel.style.display = '';
      }
    }

    const rootElement = document.getElementById('custom-mode-root');

    if (rootElement) {
      unmountComponentAtNode(rootElement);

      rootElement.parentNode.removeChild(rootElement);
    }
  };

  const onSelectCalendarCell = (momentDateObj) => {
    if (calendarMode === calendarConsts.YEAR_MODE) {
      //switching to month mode on select a year cell
      onModeChange(calendarConsts.MONTH_MODE);
      setNavigationDate(momentDateObj.local(false));
    } else if (calendarMode === calendarConsts.MONTH_MODE) {
      //switching to day mode on click a month cell
      onModeChange(calendarConsts.DAY_MODE);
      setNavigationDate(momentDateObj.local(false), calendarConsts.DAY_MODE);
    }
  };

  const onModeChange = (mode) => {
    setCalendarMode(mode);
    switchToCalendarMode(mode);
  };

  const today = () => {
    setCurrentlySelectedDate(moment());
  };

  const prev = () => {
    switch (calendarMode) {
      case calendarConsts.MONTH_MODE:
        setCurrentlySelectedDate(navigationDate.clone().subtract(1, 'months'));
        break;
      case calendarConsts.YEAR_MODE:
        setCurrentlySelectedDate(navigationDate.clone().subtract(1, 'years'));
        break;
      case calendarConsts.DAY_MODE:
      case calendarConsts.TIMELINE_MODE:
        setCurrentlySelectedDate(navigationDate.clone().subtract(1, 'day'));
        break;
      case calendarConsts.WEEK_MODE:
        setCurrentlySelectedDate(navigationDate.clone().subtract(7, 'days'));
        break;
    }
  };

  const next = () => {
    switch (calendarMode) {
      case calendarConsts.MONTH_MODE:
        setCurrentlySelectedDate(navigationDate.clone().add(1, 'months'));
        break;
      case calendarConsts.YEAR_MODE:
        setCurrentlySelectedDate(navigationDate.clone().add(1, 'years'));
        break;
      case calendarConsts.DAY_MODE:
      case calendarConsts.TIMELINE_MODE:
        setCurrentlySelectedDate(navigationDate.clone().add(1, 'day'));
        break;
      case calendarConsts.WEEK_MODE:
        setCurrentlySelectedDate(navigationDate.clone().add(7, 'days'));
        break;
    }
  };

  const handleSaveEvent = (event) => {
    const command = {
      event: event,
      type: confirmEditEventType,
    };

    return new Promise((resolve, reject) => {
      dispatch(actions.saveEvent(command)).then(() => {
        setIsEditEventModalOpen(false);
        setConfirmEditEventType(recurrence.EDIT_EVENT_TYPES.NONE);
        loadEvents(eventsSearchRequest);
        resolve();
      });
    });
  };

  const handleCancelSaveEvent = () => {
    setIsEditEventModalOpen(false);
    setConfirmEditEventType(recurrence.EDIT_EVENT_TYPES.NONE);
    setConfirmationActiontype(recurrence.CONFIRM_ACTION_TYPES.NONE);
  };

  // #region Calendar Header

  const renderCalendarHeader = ({ value, type, onChange, onTypeChange }) => {
    const month = value.clone().month();
    const year = value.clone().year();
    const months = getCalendarHeaderMonthItems(value);
    const years = getCalendarHeaderYearItems(value);

    return (
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <div>
          <Space>
            <Button size="small" onClick={() => today()}>
              Today
            </Button>
            <Radio.Group>
              <Button size="small" onClick={() => prev()}>
                <LeftOutlined />
              </Button>
              <Button size="small" onClick={() => next()}>
                <RightOutlined />
              </Button>
            </Radio.Group>

            <Text>{navigationDate.format('MMM DD, YYYY')}</Text>
          </Space>
        </div>

        <div>
          <Space>
            <Select
              size="small"
              dropdownMatchSelectWidth={false}
              getPopupContainer={rootContainer}
              value={year}
              onChange={(newYear) => {
                const now = value.clone().year(newYear);
                onChange(now);
              }}
            >
              {years.map((y) => (
                <Select.Option key={y} value={y} className="year-item"></Select.Option>
              ))}
            </Select>

            {type !== calendarConsts.YEAR_MODE ? (
              <Select
                size="small"
                dropdownMatchSelectWidth={false}
                getPopupContainer={rootContainer}
                value={month}
                onChange={(newMonth) => {
                  const now = value.clone().month(newMonth);
                  onChange(now);
                }}
              >
                {months.map((m) => (
                  <Select.Option key={m.id} value={m.id} className="month-item">
                    {m.name}
                  </Select.Option>
                ))}
              </Select>
            ) : null}

            <Radio.Group size="small" onChange={(e) => onTypeChange(e.target.value)} value={type}>
              {Object.keys(calendarConsts.POSSIBLE_MODES).map(function (k) {
                return (
                  calendarConsts.POSSIBLE_MODES[k].visible && (
                    <Radio.Button
                      size="small"
                      key={'calendar-mode-radiobutton-' + k}
                      disabled={calendarConsts.POSSIBLE_MODES[k].disabled}
                      title={calendarConsts.POSSIBLE_MODES[k].title}
                      value={calendarConsts.POSSIBLE_MODES[k].value}
                    >
                      {calendarConsts.POSSIBLE_MODES[k].name}
                    </Radio.Button>
                  )
                );
              })}
            </Radio.Group>
          </Space>
        </div>
      </div>
    );
  };

  // #endregion Calendar Header

  return (
    <Spin spinning={calendarEventsLoading} style={{ maxHeight: 'unset' }}>
      <CalendarSettings
        rootContainer={rootContainer}
        loading={calendarEventsLoading}
        settings={settings}
        onToggleEvents={onToggleEvents}
        onAddCategory={onAddCategory}
        onAddEvent={handleAddEventClick}
      />

      <Calendar
        className="calendar-main"
        type={calendarMode}
        value={navigationDate}
        mode={calendarMode}
        dateFullCellRender={(current) => dayCellRender(current)}
        monthCellRender={(current) => monthCellRender(current)}
        onSelect={(current) => onSelectCalendarCell(current)}
        headerRender={() =>
          renderCalendarHeader({ value: navigationDate, type: calendarMode, onChange: setCurrentlySelectedDate, onTypeChange: onModeChange })
        }
      />

      <EditEventModal
        rootContainer={rootContainer}
        isOpen={isEditEventModalOpen}
        title={editEventModalTitle}
        eventToEdit={eventToEdit}
        calendarCategories={calendarCategories}
        saveEventLoading={saveEventLoading}
        handleModalOk={handleSaveEvent}
        handleModalCancel={handleCancelSaveEvent}
      />
      <EventEditDeleteConfirmModal
        rootContainer={rootContainer}
        isOpen={isEventEditDeleteConfirmModalOpen}
        eventItem={eventToEdit}
        confirmationActionType={confirmationActionType}
        handleModalOk={handleConfirmModalOk}
        handleModalCancel={handleConfirmModalCancel}
      />
    </Spin>
  );
};

export default CalendarMain;
